import React from 'react';
import { connect } from 'react-redux';
import { mutateAsync, requestAsync } from 'redux-query';
import { Redirect, RouteComponentProps } from '@reach/router';
import { LinkButton } from '@el8/vital';
import Content from 'views/booking/common/Content/Content';
import AppointmentSummary from 'views/booking/AppointmentSummary/AppointmentSummary';
import NewPatientForm from 'views/booking/NewPatientForm/NewPatientForm';
import { getBookingInfo } from 'modules/booking';
import { updateSession } from 'modules/session';
import { getActivePatient, createChart } from 'modules/patient';

import {
  PatientChartInfo,
  HydratedPatientInfo,
  State,
  BookingState,
  SuccessResponse,
  LuxonFormats,
  Dispatch,
  RQResponse,
  Response,
} from 'types';
import { notify } from 'utils/notification';
import { checkResponse } from 'utils/errors';
import { getCurrentPatient } from 'modules/patient/patientQueries';
import { DateTime } from 'luxon';

type OwnProps = {
  practiceId: number;
} & RouteComponentProps;

type StateProps = {
  bookingInfo: Partial<BookingState>;
  patient: HydratedPatientInfo;
};

type DispatchProps = {
  createPatient: (
    patientInfo: Partial<PatientChartInfo>,
    practiceData: { practiceId: number; physicianId: number },
  ) => Promise<void>;
};

type Props = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: State): StateProps => ({
  bookingInfo: getBookingInfo(state),
  patient: getActivePatient(state),
});

const mapDispatchToProps = (
  dispatch: Dispatch,
  { navigate, practiceId }: OwnProps,
): DispatchProps => ({
  createPatient: async (
    newChart: Partial<PatientChartInfo>,
    practiceData: { practiceId: number; physicianId: number },
  ): Promise<void> => {
    try {
      const createChartResponse: RQResponse<Response<{}>> = await dispatch(
        mutateAsync(createChart(newChart, practiceData)),
      );
      checkResponse(createChartResponse);

      const patientResponse: RQResponse<Response<{}>> = await dispatch(
        requestAsync(getCurrentPatient(practiceId)),
      );
      checkResponse(patientResponse);

      const newChartId = (createChartResponse.body as SuccessResponse<PatientChartInfo>).data.id;
      dispatch(updateSession({ activeChartId: newChartId }));
      navigate('../confirmation');
    } catch (error) {
      notify(error.message);
    }
  },
});

const back = (): void => {
  window.history.back();
};

const AddPatient: React.FC<Props> = ({ bookingInfo, createPatient, patient, practiceId }) => {
  const handleCreatePatient = (patientInfo: Partial<PatientChartInfo>): Promise<void> =>
    createPatient(
      {
        email: patient.email,
        ...patientInfo,
        dob: DateTime.fromFormat(patientInfo.dob, LuxonFormats.UserFacingDob).toISODate(),
      },
      { physicianId: bookingInfo.physicianId, practiceId },
    );

  const { scheduledDate, physicianId, appointmentTypeId } = bookingInfo;
  if (!(scheduledDate && physicianId && appointmentTypeId)) {
    return <Redirect noThrow to={`book/${practiceId}/`} />;
  }

  if (!patient) {
    return <Redirect noThrow to={`book/${practiceId}/account`} />;
  }
  return (
    <Content>
      <LinkButton onClick={back} text="Back" />
      <AppointmentSummary practiceId={practiceId} />
      <NewPatientForm createPatient={handleCreatePatient} />
    </Content>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(AddPatient);
